/**
 * DealClassificationCard Component
 * 
 * Displays the Deal Classification (GOOD DEAL / MARGINAL / NO PASS)
 * with recommendation (PROCEED / NEGOTIATE / AVOID) and Lender Confidence Score.
 * 
 * FINAL UI: Decision → Financial Impact → Why (Bullets) → How to Improve (w/ Impact) → Path to Deal
 * 
 * This is a READ-ONLY display component - it never modifies analysis data.
 */

import React, { useState } from 'react';
import { CheckCircle2, AlertTriangle, XCircle, ShieldCheck, TrendingUp, TrendingDown, AlertCircle, ChevronDown, ChevronUp, Lightbulb, DollarSign, Hammer, Target, Database } from 'lucide-react';
import { translations } from '../i18n';

/**
 * Format currency as whole dollars (no cents)
 */
const formatWholeDollars = (value) => {
  if (value === null || value === undefined) return '0';
  // PROBLEM 1: tolerate a formatted string ("1,500,000") instead of collapsing to '0'.
  const num = typeof value === 'string' ? parseFloat(value.replace(/[$,\s]/g, '')) : value;
  if (num === null || num === undefined || isNaN(num)) return '0';
  return Math.round(num).toLocaleString('en-US', { maximumFractionDigits: 0 });
};

const DealClassificationCard = ({ 
  classification, 
  lang = 'en', 
  maoRule = 70,
  // P0-2 FIX: Accept user's min_profit directly as a prop to eliminate hardcoded fallback
  userMinProfit = null,
  userMinMargin = null 
}) => {
  const [showImprovements, setShowImprovements] = useState(true); // EXPANDED by default - critical user value
  const t = translations[lang] || translations.en;
  
  if (!classification) return null;
  
  const {
    decision,
    decision_label,
    recommendation,
    lender_confidence,
    confidence_label,
    summary,
    factors,
    metrics,
    improvement_suggestions
  } = classification;
  
  // P0-2 FIX: Calculate financial impact using DYNAMIC user criteria
  // Priority: 1) Direct prop from user criteria, 2) Backend metrics, 3) NO hardcoded fallback
  // If neither is provided, we should NOT display target-based text
  const targetProfit = userMinProfit !== null 
    ? userMinProfit 
    : (metrics?.target_profit || null);
  const currentProfit = metrics?.net_profit || 0;
  const profitGap = targetProfit !== null ? targetProfit - currentProfit : 0;
  const hasFinancialGap = decision !== 'GOOD_DEAL' && targetProfit !== null && profitGap > 0;
  
  // Log for debugging
  console.log('DealClassificationCard: targetProfit=', targetProfit, 'from prop:', userMinProfit, 'from metrics:', metrics?.target_profit);
  
  // SINGLE SOURCE OF TRUTH: Use the user's active MAO rule for ALL threshold assessments
  // This ensures consistency across Why This Deal, How to Improve, Path to Deal, and Price Exceeds Target
  const activeThreshold = maoRule; // User's selected rule (e.g., 75%)
  const warningThreshold = activeThreshold + 5; // 5% above = warning zone
  
  // Generate "Why this deal" as bullet points from summary
  const generateWhyBullets = () => {
    const bullets = [];
    
    // Bullet 1: Profit assessment
    // P0-2 FIX: Only show target-based text if we have a valid targetProfit
    if (targetProfit !== null && currentProfit >= targetProfit) {
      bullets.push({
        type: 'positive',
        text: (t.why_profit_exceeds || 'Net profit of ${profit} exceeds your ${target} target')
          .replace('${profit}', formatWholeDollars(currentProfit))
          .replace('${target}', formatWholeDollars(targetProfit))
      });
    } else if (targetProfit !== null && currentProfit > 0 && currentProfit < targetProfit) {
      bullets.push({
        type: 'warning',
        text: (t.why_profit_short || 'Net profit of ${profit} falls ~${gap} short of target')
          .replace('${profit}', formatWholeDollars(currentProfit))
          .replace('${gap}', formatWholeDollars(profitGap))
      });
    } else if (currentProfit > 0) {
      // No target available - just state the profit
      bullets.push({
        type: currentProfit > 15000 ? 'positive' : 'warning',
        text: `Net profit of $${formatWholeDollars(currentProfit)}`
      });
    } else {
      bullets.push({
        type: 'negative',
        text: (t.why_profit_negative || 'Negative profit of ${profit} – requires significant negotiation')
          .replace('${profit}', formatWholeDollars(currentProfit))
      });
    }
    
    // Bullet 2: Investment ratio assessment - USES USER'S ACTIVE THRESHOLD (not hardcoded 70%)
    const investmentPct = metrics?.investment_to_arv_pct || 0;
    if (investmentPct <= activeThreshold) {
      bullets.push({
        type: 'positive',
        text: (t.why_investment_healthy_rule || 'Total investment is {pct}% of estimated value (within your {rule}% threshold)')
          .replace('{pct}', investmentPct.toFixed(0))
          .replace('{rule}', activeThreshold)
      });
    } else if (investmentPct <= warningThreshold) {
      bullets.push({
        type: 'warning',
        text: (t.why_investment_tight_rule || 'Total investment is {pct}% of value – above your {rule}% threshold')
          .replace('{pct}', investmentPct.toFixed(0))
          .replace('{rule}', activeThreshold)
      });
    } else {
      bullets.push({
        type: 'negative',
        text: (t.why_investment_high_rule || 'Total investment is {pct}% of value – exceeds your {rule}% threshold significantly')
          .replace('{pct}', investmentPct.toFixed(0))
          .replace('{rule}', activeThreshold)
      });
    }
    
    // Bullet 3: Bottom line recommendation
    if (decision === 'GOOD_DEAL') {
      bullets.push({
        type: 'positive',
        text: t.why_meets_criteria || 'This deal meets your investment criteria – safe to proceed'
      });
    } else if (decision === 'MARGINAL') {
      bullets.push({
        type: 'warning',
        text: t.why_close_criteria || 'Close to your criteria – negotiate or reduce costs to improve'
      });
    } else {
      bullets.push({
        type: 'negative',
        text: t.why_too_risky || 'Too risky at current terms – significant changes needed'
      });
    }
    
    return bullets;
  };
  
  const whyBullets = generateWhyBullets();
  
  // Decision styling with recommendation
  const decisionStyles = {
    GOOD_DEAL: {
      bg: 'bg-gradient-to-r from-emerald-500 to-green-600',
      border: 'border-emerald-500',
      glow: 'shadow-emerald-200',
      icon: CheckCircle2,
      iconColor: 'text-emerald-100',
      label: t.deal_buy || 'BUY',
      recLabel: t.proceed,
      recBg: 'bg-emerald-100 text-emerald-800',
      textColor: 'text-emerald-700'
    },
    MARGINAL: {
      bg: 'bg-gradient-to-r from-amber-500 to-orange-500',
      border: 'border-amber-500',
      glow: 'shadow-amber-200',
      icon: AlertTriangle,
      iconColor: 'text-amber-100',
      label: t.deal_negotiate || 'NEGOTIATE',
      recLabel: t.deal_negotiate || 'NEGOTIATE',
      recBg: 'bg-amber-100 text-amber-800',
      textColor: 'text-amber-700'
    },
    NO_PASS: {
      bg: 'bg-gradient-to-r from-red-500 to-rose-600',
      border: 'border-red-500',
      glow: 'shadow-red-200',
      icon: XCircle,
      iconColor: 'text-red-100',
      // BUG FIX: the top badge previously showed "PASS" (meant "pass ON the deal" =
      // reject) which reads as "passed/approved" — contradicting the "AVOID" + "Does not
      // meet your criteria" text right below it. Show the actual verdict: AVOID.
      label: t.deal_avoid || 'AVOID',
      recLabel: t.deal_avoid || 'AVOID',
      recBg: 'bg-red-100 text-red-800',
      textColor: 'text-red-700'
    }
  };
  
  // Lender confidence styling
  const confidenceStyles = {
    HIGH: {
      bg: 'bg-emerald-100',
      text: 'text-emerald-700',
      border: 'border-emerald-300',
      label: t.deal_confidence_high || 'High'
    },
    MODERATE: {
      bg: 'bg-amber-100',
      text: 'text-amber-700',
      border: 'border-amber-300',
      label: t.deal_confidence_moderate || 'Moderate'
    },
    LOW: {
      bg: 'bg-red-100',
      text: 'text-red-700',
      border: 'border-red-300',
      label: t.deal_confidence_low || 'Low'
    }
  };
  
  // Risk level styling for factors
  const riskStyles = {
    low: { icon: CheckCircle2, color: 'text-emerald-500', bg: 'bg-emerald-50' },
    moderate: { icon: AlertCircle, color: 'text-amber-500', bg: 'bg-amber-50' },
    high: { icon: XCircle, color: 'text-red-500', bg: 'bg-red-50' }
  };
  
  // Improvement category icons
  const categoryIcons = {
    price: DollarSign,
    profit: TrendingUp,
    rehab: Hammer,
    general: Target
  };
  
  const style = decisionStyles[decision] || decisionStyles.MARGINAL;
  const confStyle = confidenceStyles[lender_confidence] || confidenceStyles.MODERATE;
  const Icon = style.icon;
  
  return (
    <div 
      className={`mb-6 rounded-xl border-2 ${style.border} ${style.glow} shadow-lg overflow-hidden`}
      data-testid="deal-classification-card"
    >
      {/* Header with Decision and Recommendation */}
      <div className={`${style.bg} px-4 sm:px-6 py-4 text-white`}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Icon className={`w-8 h-8 ${style.iconColor}`} />
            <div>
              <div className="flex items-center gap-3">
                <h3 className="text-2xl sm:text-3xl font-bold tracking-wide">
                  {style.label}
                </h3>
                <span className={`px-3 py-1 rounded-full text-sm font-bold ${style.recBg}`}>
                  {style.recLabel}
                </span>
              </div>
              <p className="text-sm opacity-90 mt-0.5">
                {t.deal_decision || 'Deal Decision'}
              </p>
            </div>
          </div>
          
          {/* Lender Confidence Badge */}
          <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${confStyle.bg} ${confStyle.border} border`}>
            <ShieldCheck className={`w-5 h-5 ${confStyle.text}`} />
            <div>
              <p className={`text-xs font-medium ${confStyle.text} uppercase tracking-wide`}>
                {t.deal_lender_confidence || 'Lender Confidence'}
              </p>
              <p className={`text-sm font-bold ${confStyle.text}`}>
                {confStyle.label}
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* FINANCIAL IMPACT STATEMENT - Prominent, short, numeric */}
      {hasFinancialGap && (
        <div className="px-4 sm:px-6 py-3 bg-gradient-to-r from-red-50 to-orange-50 border-b border-red-200">
          <div className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-red-600" />
            <p className="text-sm sm:text-base font-bold text-red-700">
              {(t.deal_below_target || 'This deal is ~${gap} below your target profit')
                .replace('${gap}', formatWholeDollars(profitGap))}
            </p>
          </div>
        </div>
      )}
      
      {/* Positive Financial Impact for GOOD_DEAL */}
      {decision === 'GOOD_DEAL' && currentProfit > 0 && (
        <div className="px-4 sm:px-6 py-3 bg-gradient-to-r from-emerald-50 to-green-50 border-b border-emerald-200">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-emerald-600" />
            <p className="text-sm sm:text-base font-bold text-emerald-700">
              {(t.deal_above_target || 'Expected profit: ${profit} (+${surplus} above target)')
                .replace('${profit}', formatWholeDollars(currentProfit))
                .replace('${surplus}', formatWholeDollars(currentProfit - targetProfit))}
            </p>
          </div>
        </div>
      )}
      
      {/* WHY THIS DEAL - 2-3 Bullet Points */}
      <div className="px-4 sm:px-6 py-4 bg-white border-b border-slate-100">
        <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
          <Target className="w-4 h-4 text-slate-500" />
          {t.deal_why_this_deal || 'Why This Deal'}
        </h4>
        <ul className="space-y-2">
          {whyBullets.map((bullet, index) => (
            <li 
              key={index}
              className={`flex items-start gap-2.5 text-sm ${
                bullet.type === 'positive' ? 'text-emerald-700' :
                bullet.type === 'warning' ? 'text-amber-700' :
                'text-red-700'
              }`}
            >
              <span className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${
                bullet.type === 'positive' ? 'bg-emerald-100' :
                bullet.type === 'warning' ? 'bg-amber-100' :
                'bg-red-100'
              }`}>
                {bullet.type === 'positive' ? (
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                ) : bullet.type === 'warning' ? (
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
                ) : (
                  <XCircle className="w-3.5 h-3.5 text-red-600" />
                )}
              </span>
              <span>{bullet.text}</span>
            </li>
          ))}
        </ul>
        
        {/* Trust Signal */}
        <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2 text-xs text-slate-500">
          <Database className="w-3.5 h-3.5" />
          <span>{t.deal_trust_signal || 'Based on real market data and comparable sales'}</span>
        </div>
      </div>
      
      {/* How to Improve This Deal - Only for MARGINAL and NO_PASS */}
      {improvement_suggestions && improvement_suggestions.length > 0 && (
        <div className="px-4 sm:px-6 py-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-200">
          <button
            onClick={() => setShowImprovements(!showImprovements)}
            className="w-full flex items-center justify-between gap-2 text-left"
            data-testid="toggle-improvements-btn"
          >
            <div className="flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-blue-600" />
              <h4 className="text-sm font-semibold text-blue-800">
                {t.deal_how_to_improve || 'How to Improve This Deal'}
              </h4>
            </div>
            {showImprovements ? (
              <ChevronUp className="w-4 h-4 text-blue-600" />
            ) : (
              <ChevronDown className="w-4 h-4 text-blue-600" />
            )}
          </button>
          
          {showImprovements && (
            <div className="mt-3 space-y-2">
              <p className="text-xs text-blue-600 mb-2">
                {t.deal_improvement_note || 'Conservative recommendations to protect your downside:'}
              </p>
              {improvement_suggestions
                .sort((a, b) => a.priority - b.priority)
                .map((suggestion, index) => {
                  const CategoryIcon = categoryIcons[suggestion.category] || Target;
                  // Calculate impact (profit increase) for rehab and price suggestions
                  const impact = suggestion.target_value && suggestion.current_value 
                    ? suggestion.current_value - suggestion.target_value 
                    : null;
                  return (
                    <div 
                      key={index}
                      className="flex items-start gap-3 py-2 px-3 bg-white rounded-lg border border-blue-200"
                      data-testid={`improvement-suggestion-${index}`}
                    >
                      <div className="p-1.5 rounded-full bg-blue-100">
                        <CategoryIcon className="w-4 h-4 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-slate-800 font-medium">
                          {suggestion.action}
                        </p>
                        {suggestion.target_value !== null && suggestion.current_value !== null && (
                          <>
                            <p className="text-xs text-slate-500 mt-1">
                              {t.common_current || 'Current'}: ${formatWholeDollars(suggestion.current_value)} → 
                              {' '}{t.common_target || 'Target'}: ${formatWholeDollars(suggestion.target_value)}
                            </p>
                            {/* Impact: Show profit increase */}
                            {impact > 0 && (
                              <p className="text-xs text-emerald-600 font-semibold mt-1">
                                💰 {t.deal_increases_profit || 'Increases profit by'} ~${formatWholeDollars(impact)}
                              </p>
                            )}
                          </>
                        )}
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        suggestion.priority === 1 
                          ? 'bg-red-100 text-red-700' 
                          : suggestion.priority === 2 
                            ? 'bg-amber-100 text-amber-700' 
                            : 'bg-slate-100 text-slate-600'
                      }`}>
                        P{suggestion.priority}
                      </span>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      )}
      
      {/* PATH TO DEAL - What gets this deal into your buy range? PRIORITIZED by impact */}
      {decision !== 'GOOD_DEAL' && (
        <div className="px-4 sm:px-6 py-4 bg-gradient-to-r from-emerald-50 to-teal-50 border-b border-emerald-200">
          <div className="flex items-center gap-2 mb-3">
            <Target className="w-5 h-5 text-emerald-600" />
            <h4 className="text-sm font-semibold text-emerald-800">
              {t.deal_path_to_deal || 'Path to Deal'}
            </h4>
          </div>
          <p className="text-xs text-emerald-600 mb-3">
            {t.deal_path_note || 'What gets this deal into your buy range?'}
          </p>
          <div className="space-y-2">
            {/* Generate path options sorted by impact (highest first) */}
            {(() => {
              // Calculate impact values for each option
              const priceSuggestion = improvement_suggestions?.find(s => s.category === 'price');
              const rehabSuggestion = improvement_suggestions?.find(s => s.category === 'rehab');
              
              const priceImpact = priceSuggestion?.current_value && priceSuggestion?.target_value 
                ? priceSuggestion.current_value - priceSuggestion.target_value 
                : (metrics?.purchase_price && metrics?.mao ? metrics.purchase_price - metrics.mao : profitGap * 0.6);
              
              const rehabImpact = rehabSuggestion?.current_value && rehabSuggestion?.target_value
                ? rehabSuggestion.current_value - rehabSuggestion.target_value
                : 0;
              
              // Estimate cash impact (financing costs typically 5-8% of purchase)
              const cashImpact = metrics?.purchase_price ? Math.round(metrics.purchase_price * 0.06) : Math.round(profitGap * 0.3);
              
              // Build options array with impacts
              const options = [
                {
                  id: 'price',
                  impact: priceImpact || profitGap,
                  label: t.deal_lower_purchase || 'Lower Purchase Price',
                  description: priceSuggestion?.action || (t.deal_negotiate_price || 'Negotiate a lower purchase price to improve your profit margin'),
                  target: priceSuggestion?.target_value || metrics?.mao,
                  targetLabel: t.deal_target_price || 'Target price',
                  impactType: 'highest'
                }
              ];
              
              // Add rehab option if applicable
              if (rehabSuggestion || rehabImpact > 5000) {
                options.push({
                  id: 'rehab',
                  impact: rehabImpact || 0,
                  label: t.deal_reduce_rehab || 'Reduce Rehab Scope',
                  description: rehabSuggestion?.action || (lang === 'en'
                    ? 'Find ways to reduce renovation costs while maintaining quality'
                    : 'Encuentra formas de reducir costos de renovación manteniendo calidad'),
                  target: rehabSuggestion?.target_value,
                  targetLabel: t.deal_target_rehab || 'Target rehab',
                  impactType: 'medium'
                });
              }
              
              // Add cash option
              options.push({
                id: 'cash',
                impact: cashImpact,
                label: t.deal_buy_cash || 'Buy with Cash',
                description: t.deal_eliminate_financing || 'Eliminating financing costs improves margins significantly',
                target: null,
                targetLabel: t.deal_cash_mode_hint || 'Use Cash Mode toggle above',
                impactType: 'lower'
              });
              
              // Sort by impact (highest first)
              options.sort((a, b) => (b.impact || 0) - (a.impact || 0));
              
              // Render sorted options
              return options.map((option, index) => (
                <div key={option.id} className="flex items-start gap-3 py-2 px-3 bg-white rounded-lg border border-emerald-200">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                    index === 0 ? 'bg-emerald-600' : index === 1 ? 'bg-emerald-500' : 'bg-emerald-400'
                  }`}>
                    <span className="text-xs font-bold text-white">{index + 1}</span>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-slate-800">{option.label}</p>
                      {index === 0 && (
                        <span className="text-xs px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded font-medium">
                          {t.deal_highest_impact || 'Highest Impact'}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-600">{option.description}</p>
                    {option.target && (
                      <p className="text-xs font-semibold text-emerald-700 mt-1">
                        → {option.targetLabel}: ${formatWholeDollars(option.target)}
                      </p>
                    )}
                    {!option.target && option.id === 'cash' && (
                      <p className="text-xs font-semibold text-emerald-700 mt-1">
                        → {option.targetLabel}
                      </p>
                    )}
                    {/* Show profit impact */}
                    {option.impact > 0 && (
                      <p className="text-xs text-emerald-600 mt-0.5">
                        💰 {t.deal_increases_profit || 'Increases profit by'} ~${formatWholeDollars(option.impact)}
                      </p>
                    )}
                  </div>
                </div>
              ));
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default DealClassificationCard;
